import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { throttle, debounce } from 'lodash';
import {
  SliderStyled,
  Wrapper,
  Buffered,
  Connect,
  Content,
  ContentWrap,
  Handler,
  SeekHover,
  VolumeButton,
  Thumb,
  Track,
  TrackContent,
  StyledButton,
  AdTimeLabel,
  TrackThumb,
} from './styles/SliderStyled';
import { isHasDesktopHover } from '../../utils/isMobile';

class Slider extends PureComponent {
  static SLIDE_PHASE = {
    START: 'start',
    UPDATE: 'update',
    END: 'end',
  };

  constructor(props) {
    super(props);
    this.state = {
      ready: false,
      trackWidth: 0,
      seeking: false,
      increment: 0,
      slidedTime: props.currentTime,
      hoverPosition: 0,
      isSliderSeeking: false,
    };
    this.lastPhase = null;
    this.seekHoverPosition = 0;
    this.trackHovered = false;
    this.lastTime = 0;
    this.throttleFunc = null;
    if (props.throttlingTime) {
      this.throttleFunc = throttle((time, phase, mobile) => {
        if (!mobile) {
          props.onChange(time, phase);
        }
      }, this.props.throttlingTime);
    }
    this.seeking = null;
    this.updatedPosition = true;
    this.playerPause = false;
    this.setSkipAdToFalse = false;
    this.hideThumbnailsByKeysTimeoutId = null;

    this.onWindowPointerMove = throttle(this.onWindowPointerMove, 100);
    this.onTrackPointerMove = reactThrottle(this.onTrackPointerMove, 100);
    this.onWindowKeyUp = null;
  }

  componentDidMount() {
    this.setTrackWidthState();
    window.addEventListener('resize', this.setTrackWidthState);
    if (this.props.type === 'seekBar') {
      this.addKeyboardListeners();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.currentTime !== this.props.currentTime) {
      this.updatedPosition = true;
      // console.log('updatedPosition', this.updatedPosition);
    }
    if (nextProps.type !== this.props.type) {
      if (nextProps.type === 'seekBar') {
        this.addKeyboardListeners();
      } else {
        this.removeKeyboardListeners();
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { getSeeking, type, paused, setSkipAdProperty } = this.props;
    const { seeking } = this.state;

    if (this.setSkipAdToFalse && !paused && paused !== prevProps.paused) {
      this.setSkipAdToFalse = false;
      window.setTimeout(() => {
        setSkipAdProperty(false);
      }, 500);
    }

    if (seeking !== prevState.seeking || type !== prevProps.type) {
      getSeeking(seeking, type || 'seekbar');
    }

    if (
      this.props.isDebouncedKeySeek !== prevProps.isDebouncedKeySeek &&
      type === 'seekBar'
    ) {
      this.addKeyboardListeners();
    }

    this.setTrackWidthState();
  }

  componentWillUnmount() {
    // try to remove all event listeners
    // just for safety...
    window.removeEventListener('resize', this.setTrackWidthState);
    window.removeEventListener('pointermove', this.onWindowPointerMove);
    window.removeEventListener('pointerup', this.onWindowPointerUp);
    window.removeEventListener('pointercancel', this.onWindowPointerUp);
    this.removeKeyboardListeners();
  }

  addKeyboardListeners = () => {
    this.removeKeyboardListeners();

    this.onWindowKeyUp = this.props.isDebouncedKeySeek
      ? debounce(this.onWindowKeyUpListener, 1000)
      : this.onWindowKeyUpListener;

    window.addEventListener('keydown', this.onWindowKeyDown);
    window.addEventListener('keyup', this.onWindowKeyUp);
  };

  removeKeyboardListeners = () => {
    window.removeEventListener('keydown', this.onWindowKeyDown);
    window.removeEventListener('keyup', this.onWindowKeyUp);
  };

  /**
   * @param {MouseEvent} event
   */
  onTrackPointerMove = (event) => {
    const position = event.clientX - this.track.getBoundingClientRect().left;
    this.toggleThumbnail(true, position); // shows thumbnail while hovering track
  };

  onTrackPointerLeave = () => {
    this.onTrackPointerMove.flush();
    this.toggleThumbnail(false); // hide thumbnail when cursor leaves track
  };

  onTrackPointerDown = (event) => {
    event.preventDefault();
    // change track position, inits mouse events to handle seeking while mouseDown
    if (event.button === 0) {
      if (this.props.isAbleToExecuteChange()) {
        this.props.handleSeeking(true);
        this.setSeeking(true);
        this.changeCurrentTimePosition(event.clientX, Slider.SLIDE_PHASE.START);
        this.onSliderSeekPause();

        window.addEventListener('pointermove', this.onWindowPointerMove);
        window.addEventListener('pointerup', this.onWindowPointerUp);
        window.addEventListener('pointercancel', this.onWindowPointerUp);
      }
    }
  };

  onWindowPointerMove = (event) => {
    if (event.buttons === 0) {
      // if 0, there are no pressed mouse buttons, so unsubscribe from events
      this.onWindowPointerUp(event);
    } else {
      // update time and thumbnail position while seeking
      const { seeking } = this.state;
      if (seeking) {
        const position = this.changeCurrentTimePosition(
          event.clientX,
          Slider.SLIDE_PHASE.UPDATE
        );
        this.toggleThumbnail(true, position);
      }
    }
  };

  onWindowPointerUp = (event) => {
    // hide thumb, set final time position after we stopped seeking
    this.onWindowPointerMove.flush();
    window.removeEventListener('pointermove', this.onWindowPointerMove);
    window.removeEventListener('pointerup', this.onWindowPointerUp);
    window.removeEventListener('pointercancel', this.onWindowPointerUp);

    this.props.handleSeeking(false);
    this.setSeeking(false);
    this.onSliderSeekPlay();
    this.changeCurrentTimePosition(event.clientX, Slider.SLIDE_PHASE.END);
    if (!this.trackHovered) {
      this.toggleThumbnail(false);
    }
  };

  onWindowKeyDown = (event) => {
    const { currentTime, max, isSeekByArrowsDisabled } = this.props;
    const { slidedTime, seeking } = this.state;
    const pivotTime = seeking ? slidedTime : currentTime;
    let newTime;

    if (isSeekByArrowsDisabled) {
      return;
    }

    switch (event.code) {
      case 'ArrowRight':
        newTime = Math.min(pivotTime + 5, max);
        break;
      case 'ArrowLeft':
        newTime = Math.max(0.5, pivotTime - 5);
        break;
    }
    if (newTime !== undefined) {
      if (this.props.isAbleToExecuteChange()) {
        this.setSeeking(true);
        const position = this.changeCurrentTimePosition(
          undefined,
          Slider.SLIDE_PHASE.UPDATE,
          newTime
        );
        window.clearTimeout(this.hideThumbnailsByKeysTimeoutId);
        this.toggleThumbnail(true, position);
      }
    }
  };

  onWindowKeyUpListener = (event) => {
    switch (event.code) {
      case 'ArrowLeft':
      case 'ArrowRight': {
        const { seeking, slidedTime } = this.state;
        if (seeking) {
          this.changeCurrentTimePosition(
            undefined,
            Slider.SLIDE_PHASE.END,
            slidedTime
          );
          this.toggleThumbnail(false);
          this.setSeeking(false);

          this.hideThumbnailsByKeysTimeoutId = window.setTimeout(
            this.toggleThumbnail,
            1000,
            false
          );
        }
        break;
      }
    }
  };

  onContentWrapperTransitionEnd = (event) => {
    // handle width update for volume slider when it has been opened
    if (event.propertyName === 'width' && this.props.type === 'volume') {
      if (this.track && this.track.offsetWidth > 0) {
        // update width only when it starts opening
        this.setTrackWidthState();
      }
    }
  };

  onSliderSeekPause = () => {
    // resolve case, when the user seeks while video is on pause
    const { paused, setSkipAdProperty } = this.props;

    this.playerPause = paused;
    if (!paused) {
      setSkipAdProperty(true);
      // pause(false);
    }
    this.setState({
      isSliderSeeking: true,
    });
  };

  onSliderSeekPlay = () => {
    // resolve case, when the user seeks while video is playing
    const { play } = this.props;

    this.setState({
      isSliderSeeking: false,
    });
    if (!this.playerPause) {
      play(false);
      this.setSkipAdToFalse = true;
    }
  };

  setTrackWidthState = () => {
    // sets track width state
    if (this.track) {
      this.setState({
        trackWidth: this.track.offsetWidth,
      });
    }
  };

  setSeeking = (state) => {
    // sets seeking state
    this.seekHoverPosition = !state ? 0 : this.seekHoverPosition;
    this.setState({
      seeking: state,
      hoverPosition: this.seekHoverPosition,
    });
  };

  getPositionStyle = () => {
    // set position for track indicator (on mobile devices)
    const { max, currentTime } = this.props;
    const { trackWidth, slidedTime } = this.state;
    let time =
      !this.updatedPosition || this.state.seeking ? slidedTime : currentTime;

    const width = (trackWidth * time) / max;

    return {
      width,
    };
  };

  getThumbHandlerPosition = () => {
    // sets position for thumbHandler
    const { max, currentTime } = this.props;
    const { trackWidth } = this.state;
    const position = trackWidth / (max / currentTime);

    return {
      transform: `translateX(${position}px)`,
    };
  };

  getSeekHoverPosition = () => {
    // sets styles for track hover
    const { trackWidth, hoverPosition } = this.state;

    return {
      transform: `scaleX(${hoverPosition / trackWidth})`,
    };
  };

  toggleThumbnail = (state, position) => {
    this.trackHovered = !state;
    if (state) {
      this.seekHoverPosition = position;
    } else {
      this.seekHoverPosition = 0;
    }
    const previewPosition = {
      relative: this.seekHoverPosition / this.state.trackWidth,
      absolute: this.seekHoverPosition,
    };

    this.props.onDisplayPreview(state, previewPosition);

    this.setState({
      hoverPosition: this.seekHoverPosition,
    });
  };

  changeCurrentTimePosition = (eventClientX, phase, newTime) => {
    const { max, onChange, threshold, isMobile } = this.props;
    const { trackWidth } = this.state;
    let time;
    let position;
    if (eventClientX !== undefined) {
      // change position when the user is seeking
      position = eventClientX - this.track.getBoundingClientRect().left;
      position = position < 0 ? 0 : position;
      position = position > trackWidth ? trackWidth : position;
      // this.seekHoverPosition = position;
      const percent = (position * 100) / trackWidth || 0;
      time = position === 0 ? 0.5 : Math.round(percent * (max / 100));
    } else if (newTime !== undefined) {
      time = newTime;
      position = (trackWidth * newTime) / max;
    } else {
      return;
    }

    let canUpdate = false;
    if (phase === Slider.SLIDE_PHASE.END) {
      canUpdate = true;
    } else {
      canUpdate = Math.abs(time - this.lastTime) > threshold;
    }

    this.lastPhase = phase;
    this.setState({
      slidedTime: time,
    });
    if (
      (this.throttleFunc || isMobile) &&
      phase === Slider.SLIDE_PHASE.UPDATE
    ) {
      this.updatedPosition = false;
      if (canUpdate && !isMobile) {
        this.throttleFunc(time, phase, isMobile);
        this.lastTime = time;
      }
    } else if (canUpdate) {
      onChange(time, phase);
      this.lastTime = time;

      if (isMobile) {
        this.toggleThumbnail(false);
      }
    }

    return position;
  };

  assignTrackRef = (ref) => {
    this.track = ref;
  };

  isThumbActive = () => {
    // set whether thumb is visible or not
    const { seeking } = this.state;

    return this.seekHoverPosition > 0 || seeking;
  };

  renderSlider = () => {
    const { seeking } = this.state;
    const {
      disable,
      loadedFraction,
      // thumbnails,
      thumbHandler,
      useBuffering,
      useHoverFill,
      isMobile,
      type,
      connectColor,
      adTimeLabels,
      max,
    } = this.props;

    return (
      <ContentWrap
        type={type}
        seeking={type === 'volume' && seeking}
        onTransitionEnd={this.onContentWrapperTransitionEnd}
        isDisabled={disable}
      >
        <Track>
          <TrackContent
            onPointerMove={this.onTrackPointerMove}
            onPointerLeave={this.onTrackPointerLeave}
            onPointerDown={this.onTrackPointerDown}
            isMobile={isMobile}
            type={type}
            ref={this.assignTrackRef}
          >
            <Wrapper
              activeThumb={this.isThumbActive()}
              isMobile={isMobile}
              type={type}
            >
              {useBuffering && (
                <Buffered
                  style={{
                    transform: `scaleX(${loadedFraction})`,
                  }}
                />
              )}
              <Connect
                isMobile={isMobile}
                connectColor={connectColor}
                style={this.getPositionStyle()}
              >
                {isMobile && <TrackThumb />}
              </Connect>
              {!isMobile && useHoverFill && (
                <SeekHover style={this.getSeekHoverPosition()} />
              )}

              {adTimeLabels &&
                Boolean(adTimeLabels.length) &&
                adTimeLabels.map(({ time }) => (
                  <AdTimeLabel key={time} point={(time * 100) / max} />
                ))}
            </Wrapper>
          </TrackContent>

          {thumbHandler && type !== 'volume' ? (
            <Thumb style={this.getThumbHandlerPosition()}>
              <Handler activeThumb={this.isThumbActive()} />
            </Thumb>
          ) : null}
        </Track>
      </ContentWrap>
    );
  };

  renderSliderByType = (type) => {
    const {
      icon,
      onVolumeIconClick,
      isMobile,
      volumeIconColor,
      volumeHoverColor,
    } = this.props;

    if (type === 'volume') {
      return (
        <Content>
          <VolumeButton>
            <StyledButton
              onClick={onVolumeIconClick}
              data-role={'volume'}
              icon={icon}
              iconColor={volumeIconColor}
              hoverColor={volumeHoverColor}
              isMobile={isMobile}
            />
          </VolumeButton>
          {!isMobile && isHasDesktopHover ? this.renderSlider() : ''}
        </Content>
      );
    }
    return <Content>{this.renderSlider()}</Content>;
  };

  render() {
    const { type } = this.props;

    return <SliderStyled>{this.renderSliderByType(type)}</SliderStyled>;
  }
}

Slider.propTypes = {
  onChange: PropTypes.func.isRequired,
  offset: PropTypes.number,
  max: PropTypes.number.isRequired,
  currentTime: PropTypes.number,
  minutesPrefix: PropTypes.string,
  secondsPrefix: PropTypes.string,
  loadedFraction: PropTypes.number,
  type: PropTypes.string,
  useBuffering: PropTypes.bool,
  useHoverFill: PropTypes.bool,
  drawThumbnail: PropTypes.bool,
  thumbHandler: PropTypes.bool,
  thumbnails: PropTypes.object,
  icon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ id: PropTypes.string }),
  ]),
  onVolumeIconClick: PropTypes.func,
  pause: PropTypes.func,
  play: PropTypes.func,
  paused: PropTypes.bool,
  getSeeking: PropTypes.func,
  isMobile: PropTypes.bool,
  throttlingTime: PropTypes.number,
  threshold: PropTypes.number,
  setSkipAdProperty: PropTypes.func,
  handleSeeking: PropTypes.func,
  connectColor: PropTypes.string,
  adTimeLabels: PropTypes.array,
  isAbleToExecuteChange: PropTypes.func,
  isDebouncedKeySeek: PropTypes.bool,
};

Slider.defaultProps = {
  currentTime: 0,
  offset: 0,
  minutesPrefix: '',
  secondsPrefix: '',
  loadedFraction: 0,
  useBuffering: false,
  useHoverFill: true,
  drawThumbnail: true,
  thumbnails: {},
  thumbHandler: false,
  type: null,
  onHover: false,
  icon: '',
  onVolumeIconClick: Function.prototype,
  play: Function.prototype,
  pause: Function.prototype,
  paused: null,
  getSeeking: Function.prototype,
  isMobile: false,
  throttlingTime: undefined,
  threshold: 0,
  setSkipAdProperty: Function.prototype,
  handleSeeking: Function.prototype,
  connectColor: '',
  adTimeLabels: [],
  onDisplayPreview: Function.prototype,
  isAbleToExecuteChange: () => true,
  isDebouncedKeySeek: false,
};

export default Slider;

/**
 * Make throttling for React event's handler
 * @return {function(*=): void}
 */
function reactThrottle() {
  const thFn = throttle.apply(null, arguments);
  handler.cancel = thFn.cancel;
  handler.flush = thFn.flush;
  return handler;

  function handler(event) {
    event.persist();
    thFn(event);
  }
}
